<svelte:options runes={false} />
<script context="module">
  // Default per-org starting cash for PAPER deploys (KTD1/U13). Live deploys default to the available balance.
  export const DEFAULT_PAPER_CASH = 1_000_000;
</script>
<script>
  // Participating-orgs allocation (#2 redesign): a professional, collapsible section that matches the dashboard
  // theme. Default COLLAPSED, with a header summarizing the selected orgs (comma-separated names) + total cash.
  // At least the user's own org is selected by default. Emits the `allocations` array the deploy contract
  // expects (KTD1): [{ orgId, startingCash }], plus a `valid` flag. Membership is re-validated server-side (U4).
  //
  // Live ceiling (#3): when `maxTotal` is a number (the owner's available USD), the section validates that
  // Σ(selected cash) ≤ maxTotal inline and flags the overflow; the server re-checks via the balance gate.
  import { createEventDispatcher } from 'svelte';
  import { formatQuoteAmount, CURRENCY_GLYPHS } from '../../lib/quoteCurrency.js';

  const dispatch = createEventDispatcher();

  export let orgs = [];                 // [{ orgId, orgName, email }]
  export let disabled = false;
  // ADR-0014: the strategy's quote currency — labels/formats every amount in this section.
  export let currency = 'USD';
  export let defaultOrgId = null;       // the org selected by default (the user's own org / selected org)
  export let defaultCash = DEFAULT_PAPER_CASH; // per-org default cash (paper $1M; live → available balance)
  export let maxTotal = null;           // optional ceiling (available USD for Live); null → no ceiling
  export let collapsedDefault = true;

  let expanded = !collapsedDefault;

  // Per-org row state keyed by orgId: { selected, cash, cashDisplay }.
  let rows = {};   // keyed by orgId: { selected, cash, cashDisplay, edited }
  let seededFor = ''; // the org-list/default-org identity the selection was seeded for

  // (1) Seed selection + initial cash when the ORG LIST or DEFAULT ORG changes (login / org refresh / mode
  // switch). Preserves existing per-org state when the same orgs persist; defaults each row's cash to the
  // current defaultCash and marks it un-edited so step (2) can re-default it as the balance/slider move.
  $: {
    const key = `${orgs.map((o) => o.orgId).join(',')}|${defaultOrgId}`;
    if (key !== seededFor) {
      const next = {};
      for (const o of orgs) {
        const prev = rows[o.orgId];
        const isDefault = defaultOrgId ? o.orgId === defaultOrgId : orgs[0]?.orgId === o.orgId;
        next[o.orgId] = prev ?? {
          selected: isDefault,
          cash: defaultCash,
          cashDisplay: formatCash(defaultCash),
          edited: false,
        };
      }
      if (orgs.length > 0 && !Object.values(next).some((r) => r.selected)) {
        next[orgs[0].orgId] = { ...next[orgs[0].orgId], selected: true };
      }
      rows = next;
      seededFor = key;
    }
  }

  // (2) Re-default the cash of UN-EDITED rows when defaultCash changes (the Live balance loads / the % slider
  // moves). User-edited rows are preserved. This is the part the first-seed `??` used to swallow — without it
  // the slider/balance had no effect on the per-org cash (a $1M paper placeholder stuck against a real balance).
  let lastDefaultCash = null;
  $: if (defaultCash !== lastDefaultCash && Object.keys(rows).length > 0) {
    lastDefaultCash = defaultCash;
    let changed = false;
    const next = { ...rows };
    for (const id of Object.keys(next)) {
      if (!next[id].edited) {
        next[id] = { ...next[id], cash: defaultCash, cashDisplay: formatCash(defaultCash) };
        changed = true;
      }
    }
    if (changed) rows = next;
  }

  function formatCash(n) {
    if (n === null || n === undefined || n === '' || isNaN(n)) return '';
    return Number(n).toLocaleString('en-US');
  }

  function toggle(orgId) {
    rows[orgId] = { ...rows[orgId], selected: !rows[orgId].selected };
    rows = rows;
  }
  function onCashInput(orgId, event) {
    const digits = event.target.value.replace(/[^\d]/g, '');
    const cash = digits === '' ? '' : Number(digits);
    // Mark the row user-edited so the defaultCash re-default (step 2) leaves it alone.
    rows[orgId] = { ...rows[orgId], cash, cashDisplay: event.target.value, edited: true };
    rows = rows;
  }
  function onCashBlur(orgId) {
    rows[orgId] = { ...rows[orgId], cashDisplay: formatCash(rows[orgId].cash) };
    rows = rows;
  }
  // Per-org amount SLIDER (Live): each org row gets its OWN slider bounded by the unreserved ceiling — never
  // one common slider for all orgs. Slider and text input stay in sync (both set `edited`).
  function onCashSlide(orgId, event) {
    const cash = Number(event.target.value);
    rows[orgId] = { ...rows[orgId], cash, cashDisplay: formatCash(cash), edited: true };
    rows = rows;
  }
  function orgName(o) { return o.orgName ?? o.orgId; }

  // Slider bounds: cap each org's slider at the ceiling (when set); $1 granularity is noise — step in $10.
  $: sliderMax = maxTotal != null ? Math.max(0, Math.floor(maxTotal)) : null;

  // The selected rows → the deploy contract's allocations[].
  $: allocations = orgs
    .filter((o) => rows[o.orgId]?.selected)
    .map((o) => ({ orgId: o.orgId, startingCash: Number(rows[o.orgId].cash) }));

  $: selectedOrgs = orgs.filter((o) => rows[o.orgId]?.selected);
  $: totalCash = allocations.reduce((sum, a) => sum + (Number(a.startingCash) || 0), 0);
  $: selectedNames = selectedOrgs.map(orgName).join(', ');

  // Validity: ≥1 selected, each cash > 0, and (when a ceiling is set) total ≤ maxTotal.
  $: hasInvalidCash = allocations.some((a) => !(a.startingCash > 0));
  $: overCeiling = maxTotal != null && totalCash > maxTotal;
  $: valid = allocations.length >= 1 && !hasInvalidCash && !overCeiling;

  $: dispatch('change', { allocations, valid });

  // ADR-0014: amounts render in the strategy's quote currency (the deploy form passes it down).
  function fmtUsd(n) {
    return formatQuoteAmount(n, currency);
  }
</script>

<div class="at-org-alloc" class:expanded class:invalid={!valid}>
  <button type="button" class="at-org-alloc-header" on:click={() => (expanded = !expanded)}
    aria-expanded={expanded} aria-controls="at-org-alloc-body">
    <span class="at-org-alloc-chevron" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
    <span class="at-org-alloc-headtext">
      <span class="at-org-alloc-label">Participating organizations &amp; capital</span>
      <span class="at-org-alloc-summary">
        {#if selectedOrgs.length === 0}
          <span class="at-org-alloc-none">None selected</span>
        {:else}
          <span class="at-org-alloc-orgs">{selectedNames}</span>
          <span class="at-org-alloc-total">· {fmtUsd(totalCash)}</span>
        {/if}
      </span>
    </span>
    {#if overCeiling}<span class="at-org-alloc-warn" title="Exceeds available balance">⚠</span>{/if}
  </button>

  {#if expanded}
    <div class="at-org-alloc-body" id="at-org-alloc-body">
      {#if orgs.length === 0}
        <p class="form-hint">No accessible organizations. You must belong to at least one org to deploy.</p>
      {:else}
        <ul class="at-org-alloc-rows">
          {#each orgs as o (o.orgId)}
            {@const row = rows[o.orgId] ?? { selected: false, cash: defaultCash, cashDisplay: '' }}
            <li class="at-org-alloc-row" class:selected={row.selected}>
              <div class="at-org-alloc-rowmain">
                <label class="at-org-alloc-check">
                  <input type="checkbox" checked={row.selected} disabled={disabled}
                    on:change={() => toggle(o.orgId)} aria-label={`Include ${orgName(o)}`} />
                  <span class="at-org-alloc-orginfo">
                    <span class="at-org-alloc-orgname">{orgName(o)}</span>
                    {#if o.email}<span class="at-org-alloc-email">{o.email}</span>{/if}
                  </span>
                </label>
                <div class="at-org-alloc-cashwrap">
                  <span class="at-org-alloc-cashsym">{CURRENCY_GLYPHS[currency] ?? currency}</span>
                  <input type="text" inputmode="numeric" class="form-input at-org-alloc-cash"
                    value={row.cashDisplay} disabled={!row.selected || disabled}
                    on:input={(e) => onCashInput(o.orgId, e)} on:blur={() => onCashBlur(o.orgId)}
                    aria-label={`Starting cash for ${orgName(o)}`}
                    aria-invalid={row.selected && !(Number(row.cash) > 0)} />
                </div>
              </div>
              <!-- Per-org amount slider (Live only): one slider PER org row, bounded by the unreserved ceiling. -->
              {#if sliderMax != null && row.selected}
                <div class="at-org-alloc-sliderwrap">
                  <input type="range" min="0" max={sliderMax} step="10"
                    value={Number(row.cash) || 0} disabled={disabled}
                    on:input={(e) => onCashSlide(o.orgId, e)}
                    aria-label={`Allocation amount for ${orgName(o)}`} />
                  <span class="at-org-alloc-sliderpct">
                    {sliderMax > 0 ? Math.round(((Number(row.cash) || 0) / sliderMax) * 100) : 0}%
                  </span>
                </div>
              {/if}
            </li>
          {/each}
        </ul>

        {#if maxTotal != null && allocations.length > 0 && !overCeiling}
          <span class="at-org-alloc-remaining">
            Allocating {fmtUsd(totalCash)} · {fmtUsd(Math.max(0, maxTotal - totalCash))} of your unreserved balance left
          </span>
        {/if}
        {#if allocations.length === 0}
          <span class="form-error">Select at least one organization to fund.</span>
        {:else if hasInvalidCash}
          <span class="form-error">Each selected organization's starting cash must be greater than zero.</span>
        {:else if overCeiling}
          <span class="form-error">Total ({fmtUsd(totalCash)}) exceeds your unreserved balance ({fmtUsd(maxTotal)}) — funds allocated to other active live strategies are reserved. Withdraw from one to free funds.</span>
        {/if}
        <span class="form-hint">
          {#if maxTotal != null}
            Each selected org funds the strategy with its own slice (one slider per org). The total must stay within your unreserved balance.
          {:else}
            Each selected org funds the strategy with its own cash slice (paper default $1,000,000).
          {/if}
        </span>
      {/if}
    </div>
  {/if}
</div>

<style>
  .at-org-alloc {
    border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 8px;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
    overflow: hidden;
  }
  .at-org-alloc.invalid { border-color: color-mix(in srgb, var(--danger, #e0533a) 40%, var(--border-subtle, #1e1f22)); }
  .at-org-alloc-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.7rem 0.85rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    color: var(--text-primary, #e8e9ea);
  }
  .at-org-alloc-header:hover { background: var(--surface-2, rgba(255, 255, 255, 0.03)); }
  .at-org-alloc-chevron { color: var(--text-muted, #8a8b8d); font-size: 0.8rem; width: 1rem; }
  .at-org-alloc-headtext { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 0; }
  .at-org-alloc-label { font-size: 0.85rem; font-weight: 600; }
  .at-org-alloc-summary { font-size: 0.78rem; color: var(--text-muted, #8a8b8d); display: flex; gap: 0.4rem; min-width: 0; }
  .at-org-alloc-orgs { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .at-org-alloc-total { color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .at-org-alloc-none { color: var(--danger, #e0533a); }
  .at-org-alloc-warn { color: var(--danger, #e0533a); margin-left: auto; }

  .at-org-alloc-body { padding: 0.5rem 0.85rem 0.85rem; border-top: 1px solid var(--border-subtle, #1e1f22); }
  .at-org-alloc-rows { list-style: none; margin: 0.4rem 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  .at-org-alloc-row {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 6px;
    background: var(--surface-0, transparent);
  }
  .at-org-alloc-rowmain { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
  .at-org-alloc-sliderwrap { display: flex; align-items: center; gap: 0.6rem; }
  .at-org-alloc-sliderwrap input[type='range'] { flex: 1; accent-color: var(--accent, #3c78f0); }
  .at-org-alloc-sliderpct { font-size: 0.75rem; color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; width: 2.6rem; text-align: right; }
  .at-org-alloc-remaining { font-size: 0.78rem; color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; }
  .at-org-alloc-row.selected { border-color: color-mix(in srgb, var(--accent, #3c78f0) 45%, var(--border-subtle, #1e1f22)); background: var(--surface-2, rgba(60, 120, 240, 0.05)); }
  .at-org-alloc-check { display: flex; align-items: center; gap: 0.55rem; cursor: pointer; min-width: 0; }
  .at-org-alloc-orginfo { display: flex; flex-direction: column; gap: 0.05rem; min-width: 0; }
  .at-org-alloc-orgname { font-weight: 600; font-size: 0.85rem; color: var(--text-primary, #e8e9ea); }
  .at-org-alloc-email { font-size: 0.72rem; color: var(--text-muted, #8a8b8d); }
  .at-org-alloc-cashwrap { display: flex; align-items: center; gap: 0.2rem; }
  .at-org-alloc-cashsym { color: var(--text-muted, #8a8b8d); font-size: 0.85rem; }
  .at-org-alloc-cash { width: 9rem; text-align: right; font-variant-numeric: tabular-nums; }
</style>
